import { VerifiedBadge } from "../components/VerifiedBadge";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Layout } from "../components/Navbar";
import { api, getUser } from "../api";
import { toast } from "sonner";
import { ShoppingCart, MapPin, Truck } from "lucide-react";
import { CartDrawer } from "../components/ShopTab";
import { cartCount } from "../lib/cart";
import { Avatar } from "../components/Posts";
import { PatientLink } from "../components/PatientLink";

const eur = (c) => `€ ${((c || 0) / 100).toFixed(2)}`;
const IMG_BASE = process.env.REACT_APP_BACKEND_URL || "";
const imgSrc = (s) => (s && s.startsWith("/api/") ? IMG_BASE + s : s);
const NEXT = { paid: "preparing", preparing: "shipped", shipped: "delivered" };

// Disponibilità prodotto: riusa la regola pubblica esistente (active:True) via GET /organizations/{oid}/products.
// activeIds === null => stato non ancora noto (nessuna etichetta, nessun link).
const prodActive = (activeIds, pid) => activeIds !== null && !!pid && activeIds.has(pid);
const prodGone = (activeIds, pid) => activeIds !== null && !!pid && !activeIds.has(pid);

function ProductLink({ activeIds, pid, className = "", children }) {
  if (prodActive(activeIds, pid))
    return <Link to={`/products/${pid}`} onClick={(e) => e.stopPropagation()} className={`${className} hover:opacity-80 focus:outline-none focus:ring-2 focus:ring-blue-400 rounded`} data-testid="order-product-link">{children}</Link>;
  return <span className={className}>{children}</span>;
}

function OrderThumb({ src, size = "w-12 h-12" }) {
  const [broken, setBroken] = useState(false);
  const s = imgSrc(src);
  if (!s || broken) return <div className={`${size} rounded bg-slate-100 shrink-0`} data-testid="order-thumb-fallback" />;
  return <img src={s} alt="" className={`${size} rounded object-cover shrink-0`} onError={() => setBroken(true)} data-testid="order-thumb" />;
}

export default function Orders() {
  const { t, i18n } = useTranslation();
  const u = getUser();
  const [orders, setOrders] = useState(null);
  const [cartOpen, setCartOpen] = useState(false);
  const [count, setCount] = useState(cartCount());
  const [activeIds, setActiveIds] = useState(null);
  const isOrg = u?.role === "organization";
  const load = () => api.get("/orders").then(({ data }) => setOrders(data)).catch(() => setOrders([]));
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (!orders) return;
    if (orders.length === 0) { setActiveIds(new Set()); return; }
    const orgIds = [...new Set(orders.map((o) => o.org_id).filter(Boolean))];
    Promise.all(orgIds.map((oid) => api.get(`/organizations/${oid}/products`).then(({ data }) => data).catch(() => [])))
      .then((lists) => { const s = new Set(); lists.flat().forEach((p) => p && s.add(p.id)); setActiveIds(s); });
  }, [orders]);
  useEffect(() => { const h = () => setCount(cartCount()); window.addEventListener("cart-changed", h); return () => window.removeEventListener("cart-changed", h); }, []);
  if (!u || (u.role !== "organization" && u.role !== "user")) return <Layout><div className="yp-container py-8">{t("shop.orders_forbidden")}</div></Layout>;

  const statusBadge = (s) => <span className={`yp-badge ${s === "delivered" ? "yp-badge-approved" : s === "cancelled" ? "yp-badge-rejected" : "yp-badge-paid"}`} data-testid="order-status">{t(`shop.st_${s}`)}</span>;

  return (
    <Layout><div className="yp-container max-w-3xl py-8" data-testid="orders-page">
      <div className="flex justify-between items-center mb-6 gap-3 flex-wrap">
        <h1 className="text-3xl font-bold">{t("shop.orders")}</h1>
        {u.role === "user" && (
          <button className="yp-btn yp-btn-outline !py-1.5 !px-4 text-sm relative" onClick={() => setCartOpen(true)} data-testid="orders-open-cart">
            <ShoppingCart className="w-4 h-4 inline mr-1" />{t("shop.cart")}
            {count > 0 && <span className="ml-1 yp-badge yp-badge-paid" data-testid="orders-cart-count">{count}</span>}
          </button>)}
      </div>
      {orders === null ? <div className="text-slate-400">{t("common.loading")}</div>
        : orders.length === 0 ? <div className="text-slate-400" data-testid="orders-empty">{t("shop.no_orders")}</div>
          : (<div className="space-y-4" data-testid="orders-list">
            {orders.map((o) => isOrg ? (
              <SellerOrderCard key={o.id} o={o} activeIds={activeIds} t={t} i18nLang={i18n.language} statusBadge={statusBadge} onChanged={load} />
            ) : (
              <UserOrderCard key={o.id} o={o} activeIds={activeIds} t={t} i18nLang={i18n.language} statusBadge={statusBadge} />
            ))}
          </div>)}
      {cartOpen && <CartDrawer onClose={() => setCartOpen(false)} />}
    </div></Layout>);
}

function UserOrderCard({ o, activeIds, t, i18nLang, statusBadge }) {
  const tk = o.tracking || {};
  return (
    <div className="yp-card p-5 sm:p-6" data-testid="order-row">
      <div className="flex justify-between items-start gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="text-lg font-bold" data-testid="order-no">{o.order_no}</div>
          <div className="flex items-center gap-1.5 mt-1 text-sm text-slate-500 flex-wrap">
            <Link to={`/organizations/${o.org_id}`} className="flex items-center gap-1.5 text-blue-600 hover:underline w-fit" data-testid="order-seller-link">
              <Avatar url={o.org_avatar_url} label={o.org_name} cls="w-5 h-5 text-[10px]" />
              <span data-testid="order-seller">{o.org_name}</span>{o.org_verified && <VerifiedBadge size={13} />}
            </Link>
            <span>· {new Date(o.created_at).toLocaleDateString(i18nLang)}</span>
          </div>
        </div>
        <div className="flex items-center gap-4">
          {statusBadge(o.status)}
          <div className="text-right border-l border-slate-100 pl-4">
            <div className="text-xs text-slate-400">{t("shop.order_total")}</div>
            <div className="text-xl font-bold" data-testid="order-total-top">{eur(o.total_cents)}</div>
          </div>
        </div>
      </div>
      <div className="mt-4 pt-4 border-t border-slate-100 space-y-3">
        {o.items.map((i, idx) => (
          <div key={idx} className="flex items-center gap-3" data-testid="order-item">
            <ProductLink activeIds={activeIds} pid={i.product_id} className="shrink-0"><OrderThumb src={i.image} size="w-14 h-14" /></ProductLink>
            <div className="flex-1 min-w-0">
              <ProductLink activeIds={activeIds} pid={i.product_id} className="font-medium block truncate max-w-full">{i.product_name}{i.variant_label ? ` · ${i.variant_label}` : ""}</ProductLink>
              <div className="text-xs text-slate-500 mt-0.5">×{i.qty}</div>
              {prodGone(activeIds, i.product_id) && <div className="text-[11px] text-slate-400 mt-0.5" data-testid="order-product-gone">{t("shop.product_gone")}</div>}
            </div>
            <div className="text-sm font-medium whitespace-nowrap">{eur((i.unit_price_cents || 0) * (i.qty || 1))}</div>
          </div>))}
      </div>
      <dl className="mt-4 pt-4 border-t border-slate-100 text-sm space-y-1.5">
        <div className="flex justify-between"><dt className="text-slate-500">{t("shop.subtotal")}</dt><dd>{eur(o.subtotal_cents)}</dd></div>
        <div className="flex justify-between"><dt className="text-slate-500">{t("shop.shipping")}</dt><dd>{eur(o.shipping_cents)}</dd></div>
        <div className="flex justify-between text-base font-bold pt-0.5"><dt>{t("shop.total")}</dt><dd className="underline" data-testid="order-total">{eur(o.total_cents)}</dd></div>
      </dl>
      <div className="mt-4 pt-4 border-t border-slate-100 grid sm:grid-cols-2 gap-4">
        <div>
          <div className="flex items-center gap-1.5 font-medium text-sm"><MapPin className="w-4 h-4 text-slate-400 shrink-0" />{t("shop.shipping_address")}</div>
          <div className="text-slate-600 text-sm mt-1" data-testid="order-address">{o.shipping_address?.name} · {o.shipping_address?.line1}, {o.shipping_address?.city} {o.shipping_address?.zip}</div>
        </div>
        {tk.code && (
          <div>
            <div className="flex items-center gap-1.5 font-medium text-sm"><Truck className="w-4 h-4 text-slate-400 shrink-0" />{t("shop.tracking")}</div>
            <div className="text-sm mt-1" data-testid="order-tracking">
              {tk.carrier ? `${tk.carrier} · ` : ""}
              {tk.url
                ? <a href={tk.url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline break-all" data-testid="order-tracking-link">{tk.code} ↗</a>
                : <span className="break-all">{tk.code}</span>}
            </div>
          </div>)}
      </div>
    </div>);
}

function SellerOrderCard({ o, activeIds, t, i18nLang, statusBadge, onChanged }) {
  const [tracking, setTracking] = useState({ carrier: o.tracking?.carrier || "", tracking_code: o.tracking?.code || "", tracking_url: o.tracking?.url || "" });
  const [busy, setBusy] = useState(false);
  const next = NEXT[o.status];
  const tk = o.tracking || {};
  const advance = async () => {
    setBusy(true);
    try {
      const body = { status: next };
      if (next === "shipped") Object.assign(body, tracking);
      await api.post(`/orders/${o.id}/status`, body);
      toast.success(t("shop.status_updated")); onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || t("events.error_generic")); setBusy(false); }
  };
  return (
    <div className="yp-card p-5 sm:p-6" data-testid="order-row">
      <div className="flex justify-between items-start gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="text-lg font-bold break-words" data-testid="order-no">{o.order_no}</div>
          <div className="flex items-center gap-1.5 mt-1 text-sm text-slate-500 flex-wrap">
            <PatientLink id={o.buyer_id} name={o.buyer_name || o.shipping_address?.name || o.user_id} avatarUrl={o.buyer_avatar_url} cls="w-5 h-5 text-[10px]" nameCls="text-sm" testid="order-buyer-link" />
            <span>· {new Date(o.created_at).toLocaleDateString(i18nLang)}</span>
          </div>
        </div>
        <div className="flex items-center gap-4">
          {statusBadge(o.status)}
          <div className="text-right border-l border-slate-100 pl-4">
            <div className="text-xs text-slate-400">{t("shop.order_total")}</div>
            <div className="text-xl font-bold" data-testid="order-total-top">{eur(o.total_cents)}</div>
          </div>
        </div>
      </div>
      <div className="mt-4 pt-4 border-t border-slate-100 space-y-3">
        {o.items.map((i, idx) => (
          <div key={idx} className="flex items-center gap-3" data-testid="order-item">
            <ProductLink activeIds={activeIds} pid={i.product_id} className="shrink-0"><OrderThumb src={i.image} size="w-14 h-14" /></ProductLink>
            <div className="flex-1 min-w-0">
              <ProductLink activeIds={activeIds} pid={i.product_id} className="font-medium block break-words max-w-full">{i.product_name}{i.variant_label ? ` · ${i.variant_label}` : ""}</ProductLink>
              <div className="text-xs text-slate-500 mt-0.5">×{i.qty}</div>
              {prodGone(activeIds, i.product_id) && <div className="text-[11px] text-slate-400 mt-0.5" data-testid="order-product-gone">{t("shop.product_gone")}</div>}
            </div>
            <div className="text-sm font-medium whitespace-nowrap">{eur((i.unit_price_cents || 0) * (i.qty || 1))}</div>
          </div>))}
      </div>
      <dl className="mt-4 pt-4 border-t border-slate-100 text-sm space-y-1.5">
        <div className="flex justify-between"><dt className="text-slate-500">{t("shop.subtotal")}</dt><dd>{eur(o.subtotal_cents)}</dd></div>
        <div className="flex justify-between"><dt className="text-slate-500">{t("shop.shipping")}</dt><dd>{eur(o.shipping_cents)}</dd></div>
        <div className="flex justify-between text-base font-bold pt-0.5"><dt>{t("shop.total")}</dt><dd className="underline" data-testid="order-total">{eur(o.total_cents)}</dd></div>
      </dl>
      <div className="mt-4 pt-4 border-t border-slate-100 grid sm:grid-cols-2 gap-4">
        <div>
          <div className="flex items-center gap-1.5 font-medium text-sm"><MapPin className="w-4 h-4 text-slate-400 shrink-0" />{t("shop.shipping_address")}</div>
          <div className="text-slate-600 text-sm mt-1 break-words" data-testid="order-address">{o.shipping_address?.name} · {o.shipping_address?.line1}, {o.shipping_address?.city} {o.shipping_address?.zip}</div>
        </div>
        {tk.code && (
          <div>
            <div className="flex items-center gap-1.5 font-medium text-sm"><Truck className="w-4 h-4 text-slate-400 shrink-0" />{t("shop.tracking")}</div>
            <div className="text-sm mt-1" data-testid="order-tracking">
              {tk.carrier ? `${tk.carrier} · ` : ""}
              {tk.url
                ? <a href={tk.url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline break-all" data-testid="order-tracking-link">{tk.code} ↗</a>
                : <span className="break-all">{tk.code}</span>}
            </div>
          </div>)}
      </div>
      {o.customer_note && (
        <div className="text-sm mt-4 p-3 bg-slate-50 rounded-lg" data-testid="order-note">
          <div className="font-medium">{t("shop.note_customer")}</div>
          <div className="text-slate-600 text-xs whitespace-pre-wrap mt-0.5 break-words">{o.customer_note}</div>
        </div>)}
      {next && (
        <div className="mt-4 border-t border-slate-100 pt-3" data-testid="order-advance-block">
          {next === "shipped" && (
            <div className="space-y-2 mb-2">
              <input className="yp-input w-full" placeholder={t("shop.carrier")} value={tracking.carrier} onChange={(e) => setTracking({ ...tracking, carrier: e.target.value })} data-testid="tracking-carrier" />
              <input className="yp-input w-full" placeholder={t("shop.tracking_code")} value={tracking.tracking_code} onChange={(e) => setTracking({ ...tracking, tracking_code: e.target.value })} data-testid="tracking-code" />
              <input className="yp-input w-full" placeholder={t("shop.tracking_url")} value={tracking.tracking_url} onChange={(e) => setTracking({ ...tracking, tracking_url: e.target.value })} data-testid="tracking-url" />
            </div>)}
          <button className="yp-btn yp-btn-primary w-full" onClick={advance} disabled={busy} data-testid="order-advance-btn">{t(`shop.action_${next}`)}</button>
        </div>)}
    </div>);
}
